import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import * as Icons from "lucide-react";
import { Star, Reveal, MaskLine, Label, GoldButton } from "../components/primitives";
import { ScriptureMarquee } from "../components/ScriptureMarquee";
import Stats from "../components/Stats";
import ServicesMarquee from "../components/ServicesMarquee";
import { SERVICES, PORTFOLIO } from "../data";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const monoScale = useTransform(scrollYProgress, [0, 1], [1, 1.4]);
  const monoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
      <motion.div
        style={{ scale: monoScale, y: monoY }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
      >
        <span className="font-serif font-[100] leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-b from-[#D4A843]/25 to-transparent text-[42vw] md:text-[30vw]">
          YT
        </span>
      </motion.div>

      <motion.div style={{ y: contentY }} className="relative z-10 text-center px-6">
        <MaskLine delay={0.2}>
          <span className="label text-gold flex items-center justify-center gap-3 mb-8">
            <Star size={10} /> Charlotte, North Carolina
          </span>
        </MaskLine>

        <h1 className="font-serif font-[200] leading-[0.85] tracking-tight text-[22vw] md:text-[16vw] lg:text-[13rem]">
          <motion.span
            className="block"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.33, 1, 0.68, 1] }}
          >
            YAHTOV
          </motion.span>
        </h1>

        <div className="mt-8 overflow-hidden">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
            className="font-serif text-2xl md:text-4xl font-[300] italic text-white/85"
          >
            Good work. <span className="text-gold">God's way.</span>
          </motion.p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
      >
        <span className="label text-white/40">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-[#D4A843] to-transparent" />
      </motion.div>
    </section>
  );
};

const Intro = () => (
  <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-40 relative z-10">
    <div className="grid md:grid-cols-12 gap-12 items-start">
      <div className="md:col-span-3">
        <Reveal><Label>Who we are</Label></Reveal>
      </div>
      <div className="md:col-span-9">
        <Reveal delay={0.1}>
          <p className="font-serif text-3xl md:text-5xl lg:text-6xl font-[300] leading-[1.15]">
            A faith-driven creative agency serving businesses in Charlotte, NC. We believe
            <span className="text-gold"> good work is an act of worship</span> &mdash; and we bring that
            conviction to every frame, every design, and every story we tell.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServicesGrid = () => (
  <section className="relative z-10 py-16">
    <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16 flex items-end justify-between">
      <div>
        <Reveal><Label>What we do</Label></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-serif text-5xl md:text-7xl font-[200] mt-6">Six disciplines,<br />one standard.</h2>
        </Reveal>
      </div>
    </div>

    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D4A843]/15 border hairline">
        {SERVICES.map((s, i) => {
          const Icon = Icons[s.icon];
          return (
            <Reveal key={s.id} delay={(i % 3) * 0.08}>
              <div data-testid={`service-card-${s.id}`} className="group bg-[#0D1220] p-10 lg:p-12 h-full hover:bg-white/[0.02] transition-colors duration-500">
                <div className="flex items-start justify-between mb-10">
                  <Icon strokeWidth={1} className="w-10 h-10 text-gold transition-transform duration-500 group-hover:-translate-y-1" />
                  <span className="font-serif text-2xl text-white/20">0{i + 1}</span>
                </div>
                <h3 className="font-serif text-3xl font-[400] mb-4">{s.title}</h3>
                <p className="text-white/50 leading-relaxed text-[15px]">{s.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const FeaturedWork = () => {
  const featured = PORTFOLIO.slice(0, 3);
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-40 relative z-10">
      <div className="flex items-end justify-between mb-16">
        <div>
          <Reveal><Label>Selected work</Label></Reveal>
          <Reveal delay={0.1}><h2 className="font-serif text-5xl md:text-7xl font-[200] mt-6">Featured</h2></Reveal>
        </div>
        <Reveal delay={0.15}>
          <Link to="/portfolio" className="hidden md:inline-flex label text-gold link-underline">View all</Link>
        </Reveal>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {featured.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link to="/portfolio" data-testid={`featured-${p.id}`} className="group block">
              <div className="overflow-hidden mb-5 aspect-[4/5] border hairline">
                <img
                  src={p.image}
                  alt={p.client}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-700"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-serif text-2xl">{p.client}</p>
                  <p className="label text-white/40 mt-1">{p.category}</p>
                </div>
                <Star size={12} className="text-gold opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const CTA = () => (
  <section className="relative z-10 py-28 md:py-40 border-t hairline text-center px-6">
    <Reveal>
      <Star size={22} className="text-gold mx-auto mb-8 star-spin" />
      <p className="label text-white/40 mb-10">Ready when you are</p>
      <div className="flex justify-center">
        <Link to="/contact"><GoldButton data-testid="home-cta">Let's work together</GoldButton></Link>
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Intro />
      <ServicesMarquee />
      <ServicesGrid />
      <Stats />
      <ScriptureMarquee />
      <FeaturedWork />
      <CTA />
    </div>
  );
}
